/**
 * Build a new proposal structure from individually picked timber lines
 * (proposal-side copies + CSV-side imports).
 */

import type { TimberListItem } from '../types';
import { createDefaultNodeSet } from '../types';
import {
  convertToTimberListItems,
  groupItemsByGroup,
  type CSVParseResult,
  type ParsedTimberItem,
} from './proposalCSVParser';
import {
  lineKey,
  normalizeDimensionsForMatch,
  normalizeStructureName,
  type ProposalLikeWithNodeSets,
} from './proposalTimberDiff';
import { recomputeStructureTotals } from './proposalTimberApply';

export interface TimberComparePickInput {
  structureName: string;
  /** When set, lines are appended to this structure instead of creating a new one. */
  targetNodeSetId?: string;
  proposalLineIds: string[];
  csvLineKeys: string[];
  state: ProposalLikeWithNodeSets;
  parsed: CSVParseResult;
}

export interface TimberComparePickResult {
  state: ProposalLikeWithNodeSets;
  newNodeSetId: string;
  lineCount: number;
}

export interface CompareConnectorPair {
  id: string;
  proposalRowId: string;
  csvRowKey: string;
}

export type CompareCsvRow = {
  item: ParsedTimberItem;
  selectionKey: string;
};

/** One row in the side-by-side compare — both sides share the same slot index. */
export type CompareAlignedSlot = {
  lineNumber?: number;
  proposalLine?: TimberListItem;
  csvRow?: CompareCsvRow;
  slotKey: string;
};

export type CompareDisplayGroup = {
  key: string;
  nodeSetId: string;
  structureName: string;
  csvGroupName?: string;
  slots: CompareAlignedSlot[];
};

function takeUnconsumed(
  candidates: TimberListItem[] | undefined,
  consumedIds: Set<string>,
): TimberListItem | undefined {
  if (!candidates?.length) return undefined;
  return candidates.find((b) => !consumedIds.has(b.id));
}

function matchProposalLineForCsvRow(
  csvItem: ParsedTimberItem,
  beforeByLineNumber: Map<number, TimberListItem>,
  beforeByProduction: Map<string, TimberListItem[]>,
  beforeBuckets: Map<string, TimberListItem[]>,
  consumedIds: Set<string>,
): TimberListItem | undefined {
  if (csvItem.lineNumber != null) {
    const byLine = beforeByLineNumber.get(csvItem.lineNumber);
    if (byLine && !consumedIds.has(byLine.id)) return byLine;
  }
  if (csvItem.productionNumber) {
    const byProd = takeUnconsumed(
      beforeByProduction.get(csvItem.productionNumber.trim()),
      consumedIds,
    );
    if (byProd) return byProd;
  }
  const k = lineKey(csvItem.pieceName, csvItem.normalizedDimensions);
  return takeUnconsumed(beforeBuckets.get(k), consumedIds);
}

/**
 * Row-align proposal and CSV lines within one structure (CSV row order, then proposal-only).
 * Mirrors the ordering contract in diffStructureLines / Match CSV order.
 */
export function buildCompareAlignedSlots(
  proposalLines: TimberListItem[],
  csvItems: CompareCsvRow[],
): CompareAlignedSlot[] {
  const beforeByLineNumber = new Map<number, TimberListItem>();
  const beforeByProduction = new Map<string, TimberListItem[]>();
  const beforeBuckets = new Map<string, TimberListItem[]>();

  for (const line of proposalLines) {
    if (line.sourceLineNumber != null) {
      beforeByLineNumber.set(line.sourceLineNumber, line);
    }
    if (line.sourceProductionNumber) {
      const prodKey = line.sourceProductionNumber.trim();
      const arr = beforeByProduction.get(prodKey) ?? [];
      arr.push(line);
      beforeByProduction.set(prodKey, arr);
    }
    const k = lineKey(line.name, line.dimensions);
    const arr = beforeBuckets.get(k) ?? [];
    arr.push(line);
    beforeBuckets.set(k, arr);
  }

  const consumedIds = new Set<string>();
  const slots: CompareAlignedSlot[] = [];

  for (const csvRow of csvItems) {
    const matched = matchProposalLineForCsvRow(
      csvRow.item,
      beforeByLineNumber,
      beforeByProduction,
      beforeBuckets,
      consumedIds,
    );
    if (matched) consumedIds.add(matched.id);
    slots.push({
      lineNumber: csvRow.item.lineNumber,
      proposalLine: matched,
      csvRow,
      slotKey: `slot-csv-${csvRow.selectionKey}`,
    });
  }

  for (const line of proposalLines) {
    if (consumedIds.has(line.id)) continue;
    slots.push({
      lineNumber: line.sourceLineNumber,
      proposalLine: line,
      slotKey: `slot-prop-${line.id}`,
    });
  }

  return slots;
}

/**
 * Unified structure order for compare-only display: CSV group order first,
 * then proposal-only structures in nodeSets order.
 */
export function buildCompareDisplayGroups(
  proposalGroups: ReturnType<typeof groupProposalTimberForDisplay>,
  csvGroups: ReturnType<typeof groupCsvItemsForDisplay>,
): CompareDisplayGroup[] {
  const csvByNorm = new Map<string, (typeof csvGroups)[number]>();
  const proposalByNorm = new Map<string, (typeof proposalGroups)[number]>();

  for (const group of csvGroups) {
    csvByNorm.set(normalizeStructureName(group.groupName), group);
  }
  for (const group of proposalGroups) {
    const norm = normalizeStructureName(group.structureName);
    if (!proposalByNorm.has(norm)) {
      proposalByNorm.set(norm, group);
    }
  }

  const orderedNormNames: string[] = [];
  for (const group of csvGroups) {
    const norm = normalizeStructureName(group.groupName);
    if (!orderedNormNames.includes(norm)) orderedNormNames.push(norm);
  }
  for (const group of proposalGroups) {
    const norm = normalizeStructureName(group.structureName);
    if (!orderedNormNames.includes(norm)) orderedNormNames.push(norm);
  }

  return orderedNormNames.map((norm) => {
    const proposalGroup = proposalByNorm.get(norm);
    const csvGroup = csvByNorm.get(norm);
    const structureName = proposalGroup?.structureName ?? csvGroup!.groupName;

    let slots: CompareAlignedSlot[];
    if (proposalGroup && csvGroup) {
      slots = buildCompareAlignedSlots(proposalGroup.lines, csvGroup.items);
    } else if (proposalGroup) {
      slots = proposalGroup.lines.map((line) => ({
        lineNumber: line.sourceLineNumber,
        proposalLine: line,
        slotKey: `slot-prop-${line.id}`,
      }));
    } else {
      slots = csvGroup!.items.map(({ item, selectionKey }) => ({
        lineNumber: item.lineNumber,
        csvRow: { item, selectionKey },
        slotKey: `slot-csv-${selectionKey}`,
      }));
    }

    return {
      key: norm,
      nodeSetId: proposalGroup?.nodeSetId ?? `csv-only-${csvGroup!.groupName}`,
      structureName,
      csvGroupName: csvGroup?.groupName,
      slots,
    };
  });
}

function freshLineId(prefix: string, salt: number): string {
  return `${prefix}-${Date.now()}-${salt}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Match key for side-by-side compare connectors (name + size + quantity). */
export function timberRowMatchKey(name: string, dimensions: string, quantity: number): string {
  return `${(name || '').toLowerCase().trim()}|${normalizeDimensionsForMatch(dimensions)}|${Number(quantity) || 0}`;
}

/** Stable selection key for a CSV row within its group. */
export function buildCsvLineSelectionKey(
  group: string,
  item: ParsedTimberItem,
  indexWithinGroup: number,
): string {
  return `${group}|line:${item.lineNumber}|${indexWithinGroup}`;
}

/** Index every CSV row by the same selection keys the picker UI uses. */
export function indexCsvLinesBySelectionKey(parsed: CSVParseResult): Map<string, ParsedTimberItem> {
  const map = new Map<string, ParsedTimberItem>();
  for (const group of groupCsvItemsForDisplay(parsed)) {
    for (const { item, selectionKey } of group.items) {
      map.set(selectionKey, item);
    }
  }
  return map;
}

function compareCsvItemsByStoreyThenLine(a: ParsedTimberItem, b: ParsedTimberItem): number {
  const storeyCmp = (a.storey || '').localeCompare(b.storey || '', undefined, { numeric: true });
  if (storeyCmp !== 0) return storeyCmp;
  return (a.lineNumber || 0) - (b.lineNumber || 0);
}

/** Group CSV items by group name for UI display. */
export function groupCsvItemsForDisplay(parsed: CSVParseResult): Array<{
  groupName: string;
  items: Array<{ item: ParsedTimberItem; selectionKey: string }>;
}> {
  const grouped = groupItemsByGroup(parsed.items);
  return Object.entries(grouped).map(([groupName, items]) => {
    const sorted = [...items].sort(compareCsvItemsByStoreyThenLine);
    return {
      groupName,
      items: sorted.map((item, idx) => ({
        item,
        selectionKey: buildCsvLineSelectionKey(groupName, item, idx),
      })),
    };
  });
}

/** Proposal structures that contain timber lines, for the left panel. */
export function groupProposalTimberForDisplay(state: ProposalLikeWithNodeSets): Array<{
  nodeSetId: string;
  structureName: string;
  lines: TimberListItem[];
}> {
  return state.nodeSets
    .map((ns) => ({
      nodeSetId: ns.id,
      structureName: ns.name,
      lines: ns.materials?.timberList || [],
    }))
    .filter((g) => g.lines.length > 0);
}

/** Structure-aware connector pairs for side-by-side compare. */
export function buildCompareConnectorPairs(
  proposalGroups: ReturnType<typeof groupProposalTimberForDisplay>,
  csvGroups: ReturnType<typeof groupCsvItemsForDisplay>,
  collapsedProposal: Set<string>,
  collapsedCsv: Set<string>,
): CompareConnectorPair[] {
  const csvByStructure = new Map<string, ReturnType<typeof groupCsvItemsForDisplay>[number]>();
  for (const group of csvGroups) {
    csvByStructure.set(normalizeStructureName(group.groupName), group);
  }

  const pairs: CompareConnectorPair[] = [];
  const consumedCsvKeys = new Set<string>();

  for (const proposalGroup of proposalGroups) {
    if (collapsedProposal.has(proposalGroup.nodeSetId)) continue;
    const csvGroup = csvByStructure.get(normalizeStructureName(proposalGroup.structureName));
    if (!csvGroup || collapsedCsv.has(csvGroup.groupName)) continue;

    const proposalKeyCounts = new Map<string, number>();

    for (const line of proposalGroup.lines) {
      if (line.sourceLineNumber != null) {
        const byLine = csvGroup.items.find(
          (row) => !consumedCsvKeys.has(row.selectionKey) && row.item.lineNumber === line.sourceLineNumber,
        );
        if (byLine) {
          consumedCsvKeys.add(byLine.selectionKey);
          pairs.push({ id: `${line.id}::${byLine.selectionKey}`, proposalRowId: line.id, csvRowKey: byLine.selectionKey });
          continue;
        }
      }

      if (line.sourceProductionNumber) {
        const prod = line.sourceProductionNumber.trim();
        const byProd = csvGroup.items.find(
          (row) => !consumedCsvKeys.has(row.selectionKey) && row.item.productionNumber?.trim() === prod,
        );
        if (byProd) {
          consumedCsvKeys.add(byProd.selectionKey);
          pairs.push({ id: `${line.id}::${byProd.selectionKey}`, proposalRowId: line.id, csvRowKey: byProd.selectionKey });
          continue;
        }
      }

      const matchKey = timberRowMatchKey(line.name, line.dimensions, line.quantity);
      const matchIndex = proposalKeyCounts.get(matchKey) ?? 0;
      proposalKeyCounts.set(matchKey, matchIndex + 1);

      const csvMatchCounts = new Map<string, number>();
      let csvKey: string | undefined;
      for (const row of csvGroup.items) {
        if (consumedCsvKeys.has(row.selectionKey)) continue;
        const csvMatchKey = timberRowMatchKey(row.item.pieceName, row.item.normalizedDimensions, row.item.quantity);
        const csvIndex = csvMatchCounts.get(csvMatchKey) ?? 0;
        csvMatchCounts.set(csvMatchKey, csvIndex + 1);
        if (csvMatchKey === matchKey && csvIndex === matchIndex) {
          csvKey = row.selectionKey;
          break;
        }
      }

      if (csvKey) {
        consumedCsvKeys.add(csvKey);
        pairs.push({ id: `${line.id}::${csvKey}`, proposalRowId: line.id, csvRowKey: csvKey });
      }
    }
  }

  return pairs;
}

export function buildStructureFromComparePick(
  input: TimberComparePickInput,
): TimberComparePickResult {
  const { structureName, targetNodeSetId, proposalLineIds, csvLineKeys, state, parsed } = input;
  const trimmedName = structureName.trim();

  const proposalIdSet = new Set(proposalLineIds);
  const pickedLines: TimberListItem[] = [];

  for (const ns of state.nodeSets) {
    for (const line of ns.materials.timberList || []) {
      if (!proposalIdSet.has(line.id)) continue;
      if (targetNodeSetId && ns.id === targetNodeSetId) continue;
      pickedLines.push({
        ...line,
        id: freshLineId('timber-pick', pickedLines.length),
      });
    }
  }

  const csvIndex = indexCsvLinesBySelectionKey(parsed);
  const csvParsed: ParsedTimberItem[] = [];
  for (const key of csvLineKeys) {
    const item = csvIndex.get(key);
    if (item) csvParsed.push(item);
  }
  const csvConverted = convertToTimberListItems(csvParsed);
  csvConverted.forEach((item, i) => {
    pickedLines.push({
      ...item,
      id: freshLineId('timber-csv', pickedLines.length + i),
    });
  });

  if (pickedLines.length === 0) {
    throw new Error('No timber lines selected');
  }

  if (targetNodeSetId) {
    const target = state.nodeSets.find(ns => ns.id === targetNodeSetId);
    if (!target) {
      throw new Error('Structure not found');
    }

    const nodeSets = state.nodeSets.map(ns => {
      if (ns.id !== targetNodeSetId) return ns;
      return recomputeStructureTotals({
        ...ns,
        materials: {
          ...ns.materials,
          timberList: [...(ns.materials.timberList || []), ...pickedLines],
        },
      });
    });

    return {
      state: { ...state, nodeSets },
      newNodeSetId: targetNodeSetId,
      lineCount: pickedLines.length,
    };
  }

  if (!trimmedName) {
    throw new Error('Structure name is required');
  }

  let nodeSet = createDefaultNodeSet(trimmedName);
  nodeSet = {
    ...nodeSet,
    id: `nodeset-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    materials: {
      ...nodeSet.materials,
      timberList: pickedLines,
    },
  };
  nodeSet = recomputeStructureTotals(nodeSet);

  return {
    state: {
      ...state,
      nodeSets: [...state.nodeSets, nodeSet],
    },
    newNodeSetId: nodeSet.id,
    lineCount: pickedLines.length,
  };
}
